import { useAppState } from 'lib/app-state';
import {
  formatServerDate,
  formatServerTime,
  formatLocalDate,
  formatLocalTime,
  getLocalTimezoneAbbr,
} from 'lib/helpers/date-helpers';

type DateInput = string | Date;

/**
 * Hook that provides timezone-aware date formatting functions.
 *
 * - `formatDate` / `formatTime` — format in the server/venue timezone (for event dates, order dates)
 * - `formatClientDate` / `formatClientTime` — format in the user's browser timezone (for sale start/end)
 * - `getClientTimezoneAbbr` — timezone abbreviation for the user's browser (e.g. "GMT", "CET")
 * - `timezone` — the raw server timezone string (e.g. "Atlantic/Reykjavik")
 */
export function useServerTimezone() {
  const { locale, appSettings } = useAppState();
  const timezone = appSettings?.timezone ?? 'UTC';

  return {
    timezone,

    formatDate(value: DateInput, options?: Intl.DateTimeFormatOptions) {
      return formatServerDate(value, locale, timezone, options);
    },

    formatTime(value: DateInput, options?: Intl.DateTimeFormatOptions) {
      return formatServerTime(value, locale, timezone, options);
    },

    formatClientDate(value: DateInput, options?: Intl.DateTimeFormatOptions) {
      return formatLocalDate(value, locale, options);
    },

    formatClientTime(value: DateInput, options?: Intl.DateTimeFormatOptions) {
      return formatLocalTime(value, locale, options);
    },

    getClientTimezoneAbbr(value: DateInput) {
      return getLocalTimezoneAbbr(value, locale);
    },
  };
}
